import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';

export const MENU_ROUTE_SLUGS = {
  home: 'home',
  belts: 'belts',
  events: 'events',
  students: 'students',
  history: 'history',
  contact: 'contact',
};

const FALLBACK_ITEMS = [
  {
    slug: 'home',
    label: 'Inicio',
    path: '/',
    icon: 'home',
    card_description: null,
    show_on_home: false,
    show_in_bottom_nav: true,
  },
  {
    slug: 'belts',
    label: 'Cinturones',
    path: '/belts',
    icon: 'belts',
    card_description: 'Sistema de graduación F.A.K.B (W.A.K.O.).',
    show_on_home: true,
    show_in_bottom_nav: true,
  },
  {
    slug: 'events',
    label: 'Eventos',
    path: '/events',
    icon: 'events',
    card_description: 'Exámenes, torneos y actividades.',
    show_on_home: true,
    show_in_bottom_nav: true,
  },
  {
    slug: 'students',
    label: 'Alumnos',
    path: '/students',
    icon: 'students',
    card_description: 'Comunidad y logros del equipo.',
    show_on_home: true,
    show_in_bottom_nav: true,
  },
  {
    slug: 'history',
    label: 'Historia',
    path: '/history',
    icon: 'history',
    card_description: null,
    show_on_home: false,
    show_in_bottom_nav: false,
  },
  {
    slug: 'contact',
    label: 'Contacto',
    path: '/contact',
    icon: 'contact',
    card_description: null,
    show_on_home: false,
    show_in_bottom_nav: false,
  },
];

const MenuVisibilityContext = createContext({
  ready: false,
  menuItems: FALLBACK_ITEMS,
  visibleSlugs: new Set(FALLBACK_ITEMS.map((i) => i.slug)),
  isPathAllowed: () => true,
  refresh: async () => {},
});

export function slugForPathname(pathname) {
  if (!pathname || pathname === '/') return MENU_ROUTE_SLUGS.home;
  if (pathname.startsWith('/belts')) return MENU_ROUTE_SLUGS.belts;
  if (pathname.startsWith('/events')) return MENU_ROUTE_SLUGS.events;
  if (pathname.startsWith('/students')) return MENU_ROUTE_SLUGS.students;
  if (pathname.startsWith('/history')) return MENU_ROUTE_SLUGS.history;
  if (pathname.startsWith('/contact')) return MENU_ROUTE_SLUGS.contact;
  return null;
}

export function MenuVisibilityProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [menuItems, setMenuItems] = useState(FALLBACK_ITEMS);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/public/menu');
      const items = res.data?.data;
      if (Array.isArray(items)) {
        setMenuItems(
          items.map((item) => ({
            slug: item.slug,
            label: item.label,
            path: item.path,
            icon: item.icon || item.slug,
            card_description: item.card_description ?? null,
            show_on_home: Boolean(item.show_on_home),
            show_in_bottom_nav: Boolean(item.show_in_bottom_nav),
          })),
        );
      }
    } catch {
      setMenuItems(FALLBACK_ITEMS);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const visibleSlugs = useMemo(() => new Set(menuItems.map((i) => i.slug)), [menuItems]);

  const isPathAllowed = useCallback(
    (pathname) => {
      const slug = slugForPathname(pathname);
      if (!slug) return true;
      return visibleSlugs.has(slug);
    },
    [visibleSlugs],
  );

  const value = useMemo(
    () => ({ ready, menuItems, visibleSlugs, isPathAllowed, refresh }),
    [ready, menuItems, visibleSlugs, isPathAllowed, refresh],
  );

  return (
    <MenuVisibilityContext.Provider value={value}>{children}</MenuVisibilityContext.Provider>
  );
}

export function useMenuVisibility() {
  return useContext(MenuVisibilityContext);
}

export function MenuRouteGate({ children }) {
  const location = useLocation();
  const { ready, isPathAllowed } = useMenuVisibility();

  if (!ready) {
    return (
      <div className="section-pad mx-auto max-w-content text-center text-sm text-muted" role="status">
        Cargando…
      </div>
    );
  }

  if (!isPathAllowed(location.pathname)) {
    return (
      <div className="section-pad mx-auto max-w-content text-center">
        <h1 className="page-title">404</h1>
        <p className="page-lead mx-auto">Esta sección no está disponible.</p>
        <Link to="/" className="btn-primary mt-8 inline-flex">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return children;
}
